import type { LucideIcon } from "lucide-react";
import type { ExecutionStatus } from "@/api/model/irrigation-plan-execution-report";

import { STATUS_CONFIG } from "../constants";
import type { ReportItem } from "../types";
import { getEffectiveStatusConfig } from "../utils";

interface StatusBadgeProps {
  status: ExecutionStatus | undefined;
  missedIrrigationSteps?: string[];
  missedFertigationSteps?: string[];
}

/**
 * Reusable status badge component for displaying execution status
 */
export function StatusBadge({ status, missedIrrigationSteps = [], missedFertigationSteps = [] }: StatusBadgeProps) {
  if (!status) {
    return null;
  }

  // Determine if we should show "Parcial" status
  const hasProblems = status === "COMPLETED" && (missedIrrigationSteps.length > 0 || missedFertigationSteps.length > 0);

  const config = hasProblems ? STATUS_CONFIG.PROBLEMAS : (STATUS_CONFIG[status] ?? STATUS_CONFIG.TIMED_OUT);
  const StatusIcon = config.icon as LucideIcon;

  return (
    <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
      <StatusIcon className="h-3 w-3" />
      {hasProblems ? "Parcial" : config.label}
    </div>
  );
}

interface StatusBadgeFromItemProps {
  item: ReportItem;
}

/**
 * Status badge component that derives its state from a report item
 */
export function StatusBadgeFromItem({ item }: StatusBadgeFromItemProps) {
  const effectiveStatusConfig = getEffectiveStatusConfig(item);
  const StatusIcon = effectiveStatusConfig.icon as LucideIcon;

  return (
    <div
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${effectiveStatusConfig.color}`}
    >
      <StatusIcon className="h-3 w-3" />
      {effectiveStatusConfig.label}
    </div>
  );
}

/**
 * Get row background color class based on effective status
 */
export function getRowColorClass(effectiveStatus: ExecutionStatus | "PROBLEMAS"): string {
  switch (effectiveStatus) {
    case "COMPLETED":
      return "bg-green-50";
    case "RUNNING":
      return "bg-blue-50";
    case "MISSED":
      return "bg-red-50";
    case "TIMED_OUT":
      return "bg-orange-50";
    case "PROBLEMAS":
      return "bg-yellow-50";
    default:
      return "bg-white";
  }
}
